import { useCallback, useEffect } from 'react';
import { printReceipt as sendPrintReceipt } from '../../../../services/localPrintService';
import { ResolvedReceiptHeader } from '../utils/receiptHeader';

// ═══════════════════════════════════════════════════════════
// 🍳 PAKET SİPARİŞ MUTFAK FİŞİ YAZDIRMA SERVİSİ
// ═══════════════════════════════════════════════════════════
// Bu servis paket siparişleri için mutfak fişi yazdırmak için kullanılır.
// Restoran sistemindeki KitchenPrintService ile aynı şablonu kullanır.
// Ek olarak müşteri bilgileri (adres, telefon) gösterilir.

// ═══════════════════════════════════════════════════════════
// 📦 TYPE DEFINITIONS
// ═══════════════════════════════════════════════════════════

// Mutfak fişi için alt ürün tipi (menü içerikleri)
export interface PackageKitchenOrderSubItem {
  id: string;
  name: string;
  quantity: number;
  isExtraOption?: boolean;
}

// Mutfak fişi için ürün tipi
export interface PackageKitchenOrderItem {
  id: string;
  name: string;
  quantity: number;
  note?: string;
  /**
   * Ürün açıklaması (Trendyol `productDescription`, Yemeksepeti
   * `variationName` vb.). Mutfak fişinde ürün adının altında küçük puntoyla
   * 4 boşluk girintiyle basılır (local-service/main.js satır 2367-2374).
   */
  description?: string;
  categoryId?: string;
  categoryName?: string;
  subItems?: PackageKitchenOrderSubItem[];
}

// Paket sipariş mutfak fişi verisi
export interface PackageKitchenReceiptData {
  orderNumber: string;           // Sipariş numarası (PS-XXXXXX)
  shortCode?: string;            // Kısa kod
  customerName: string;          // Müşteri adı
  customerPhone?: string;        // Müşteri telefonu
  customerAddress?: string;      // Teslimat adresi
  items: PackageKitchenOrderItem[];
  orderNote?: string;            // Genel sipariş notu
  companyName?: string;          // Firma adı
  orderTime?: string;            // Sipariş zamanı
  isAdditionalOrder?: boolean;   // İlave sipariş mi?
  isCancellationOrder?: boolean; // İptal siparişi mi?
  /**
   * Siparişin markasına göre çözülmüş fiş başlığı. Verilmezse ekran ayarındaki
   * varsayılan başlık kullanılır (bugünkü davranış).
   */
  header?: ResolvedReceiptHeader;
}

/**
 * Fişin başlık alanlarını üretir: markaya özel başlık verilmişse o, yoksa
 * ekran ayarındaki varsayılan. Üç ayrı yazdırma yolunda da aynı olması için
 * tek yerde tutuluyor.
 */
const buildHeaderFields = (
  header: ResolvedReceiptHeader | undefined,
  rds: any,
) => ({
  headerType: header?.headerType ?? rds?.headerType,
  logoUrl: header?.logoUrl ?? rds?.logoUrl,
  logoMaxWidthPx: header?.logoMaxWidthPx ?? rds?.logoMaxWidthPx ?? 384,
  logoMaxHeightPx: header?.logoMaxHeightPx ?? rds?.logoMaxHeightPx ?? 150,
  companyInfo: header?.companyInfo
    ?? (rds ? { name: rds.companyName || '', address: rds.companyAddress || '', taxNumber: rds.taxNumber || '' } : undefined),
});

// Yazıcı tanımlama için tip
interface DefinedPrinter {
  id: string;
  name: string;
}

// Servis ayarları
interface PackageKitchenPrintServiceProps {
  companyId: string | null;
  screenId?: string | null;
  screenSettings?: any;
  localServiceUrl?: string;
  onPrintError?: (error: string) => void;
  onPrintSuccess?: (message: string) => void;
}

// Hook dönüş tipi
interface PackageKitchenPrintServiceReturn {
  printPackageKitchenReceipt: (receiptData: PackageKitchenReceiptData) => Promise<void>;
  printPackageKitchenReceiptByCategory: (receiptData: PackageKitchenReceiptData) => Promise<void>;
}

// ═══════════════════════════════════════════════════════════
// 🖨️ CONSTANTS
// ═══════════════════════════════════════════════════════════

const DEFAULT_LOCAL_SERVICE_URL = 'http://localhost:3003';

// ═══════════════════════════════════════════════════════════
// 📝 HTML TEMPLATE FUNCTIONS
// ═══════════════════════════════════════════════════════════

/**
 * Paket sipariş mutfak fişi HTML şablonunu oluşturur
 * Restoran KitchenPrintService ile aynı şablon + müşteri/adres bilgisi
 */
const createPackageKitchenReceiptHtml = (receiptData: PackageKitchenReceiptData): string => {
  const currentTime = receiptData.orderTime || new Date().toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Istanbul'
  });

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Paket Sipariş - Mutfak Fişi</title>
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: 'Poppins', sans-serif;
          font-size: 9pt;
          width: 72mm;
          max-width: 72mm;
          margin: 0;
          padding: 1mm 2mm 2mm 2mm;
          box-sizing: border-box;
          line-height: 1.2;
        }
        .header {
          text-align: center;
          border-bottom: 1px dashed #000;
          padding-bottom: 4px;
          margin-bottom: 4px;
        }
        .header h1 {
          margin: 0 0 2px 0;
          font-size: 11pt;
          font-weight: bold;
          text-align: center;
        }
        .header .company-name {
          font-size: 9pt;
          margin: 0;
          font-weight: normal;
          text-align: center;
        }
        .order-type-badge {
          background-color: #000;
          color: #fff;
          padding: 2px 8px;
          font-size: 10pt;
          font-weight: bold;
          display: inline-block;
          margin: 3px 0;
          text-align: center;
        }
        .order-info {
          text-align: center;
          margin-bottom: 6px;
        }
        .order-number {
          font-size: 14pt;
          font-weight: bold;
          letter-spacing: 0;
          margin: 3px auto;
          padding: 3px 6px;
          border: 1px solid #000;
          display: inline-block;
          text-align: center;
        }
        .order-time {
          font-size: 9pt;
          color: #000;
          font-weight: bold;
          margin: 2px 0;
          text-align: center;
        }
        .customer-info {
          border: 1px solid #000;
          padding: 4px;
          margin: 4px 0;
          background-color: #f5f5f5;
        }
        .customer-info-title {
          font-size: 8pt;
          font-weight: bold;
          margin: 0 0 3px 0;
          color: #000;
          text-align: center;
          border-bottom: 1px dotted #000;
          padding-bottom: 2px;
        }
        .customer-name {
          font-size: 10pt;
          font-weight: bold;
          margin: 2px 0;
        }
        .customer-phone {
          font-size: 9pt;
          margin: 2px 0;
        }
        .customer-address {
          font-size: 9pt;
          margin: 3px 0 0 0;
          padding-top: 3px;
          border-top: 1px dotted #999;
          word-wrap: break-word;
        }
        .items-section {
          border-top: 1px dashed #000;
          border-bottom: 1px dashed #000;
          padding: 4px 0;
          margin: 4px 0;
        }
        .item {
          display: flex;
          align-items: flex-start;
          margin-bottom: 4px;
          padding-bottom: 3px;
          border-bottom: 1px dotted #999;
        }
        .item:last-child {
          border-bottom: none;
          margin-bottom: 0;
          padding-bottom: 0;
        }
        .item-quantity {
          font-size: 12pt;
          font-weight: bold;
          min-width: 24px;
          text-align: center;
          background-color: #000;
          color: #fff;
          padding: 1px 3px;
          margin-right: 5px;
        }
        .item-details {
          flex: 1;
          min-width: 0;
        }
        .item-name {
          font-size: 10pt;
          font-weight: bold;
          margin: 0;
          word-wrap: break-word;
        }
        .item-note {
          font-size: 8pt;
          color: #000;
          font-style: italic;
          margin: 2px 0 0 0;
          padding-left: 3px;
          border-left: 1px solid #000;
        }
        .sub-items {
          margin-left: 29px;
          margin-top: 2px;
        }
        .sub-item {
          font-size: 8pt;
          color: #000;
          margin: 1px 0;
          padding-left: 5px;
          border-left: 2px solid #666;
        }
        .sub-item-name {
          margin: 0;
        }
        .sub-item.extra-option {
          font-weight: bold;
          border-left-color: #000;
        }
        .order-note {
          border: 1px solid #000;
          padding: 4px;
          margin: 4px 0;
        }
        .order-note-title {
          font-size: 8pt;
          font-weight: bold;
          margin: 0 0 2px 0;
          color: #000;
        }
        .order-note-text {
          font-size: 9pt;
          margin: 0;
          font-style: italic;
        }
        .footer {
          text-align: center;
          margin-top: 4px;
          padding-top: 4px;
          border-top: 1px dashed #000;
        }
        .footer p {
          margin: 2px 0;
          font-size: 9pt;
          color: #000;
          font-weight: bold;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>${receiptData.isCancellationOrder ? '❌ İPTAL SİPARİŞ ❌' : receiptData.isAdditionalOrder ? '⚠️ İLAVE SİPARİŞ ⚠️' : /D-\d+$/.test(receiptData.shortCode || receiptData.orderNumber || '') ? '🖥️ KİOSK SİPARİŞİ - SELF SERVİS' : /P-\d+$/.test(receiptData.shortCode || receiptData.orderNumber || '') ? '🖥️ KİOSK SİPARİŞİ - PAKET' : receiptData.customerName?.includes('Masa Siparişi') ? '🪑 MASA SİPARİŞİ' : '📦 PAKET SİPARİŞ'}</h1>
        ${receiptData.companyName ? `<p class="company-name">${receiptData.companyName}</p>` : ''}
      </div>
      
      <div class="order-info">
        <div class="order-number">${(() => { const code = receiptData.shortCode || receiptData.orderNumber || ''; const m = code.match(/[DP]-\d+$/); return m ? m[0] : code; })()}</div>
        <div class="order-time">${currentTime}</div>
      </div>
      
      <!-- Müşteri Bilgileri -->
      <div class="customer-info">
        <p class="customer-info-title">${/[DP]-\d+$/.test(receiptData.shortCode || receiptData.orderNumber || '') ? '🖥️ KİOSK BİLGİLERİ' : '📍 TESLİMAT BİLGİLERİ'}</p>
        <p class="customer-name">👤 ${receiptData.customerName}</p>
        ${receiptData.customerPhone ? `<p class="customer-phone">📞 ${receiptData.customerPhone}</p>` : ''}
        ${receiptData.customerAddress ? `<p class="customer-address">📍 ${receiptData.customerAddress}</p>` : ''}
      </div>
      
      <div class="items-section">
        ${receiptData.items.map(item => `
          <div class="item">
            <div class="item-quantity">${item.quantity}</div>
            <div class="item-details">
              <p class="item-name">${item.name}</p>
              ${item.note ? `<p class="item-note">${item.note}</p>` : ''}
              ${item.subItems && item.subItems.length > 0 ? `
                <div class="sub-items">
                  ${item.subItems.map(subItem => `
                    <div class="sub-item${subItem.isExtraOption ? ' extra-option' : ''}">
                      <p class="sub-item-name">- ${subItem.name}${subItem.quantity > 1 ? ` x${subItem.quantity}` : ''}</p>
                    </div>
                  `).join('')}
                </div>
              ` : ''}
            </div>
          </div>
        `).join('')}
      </div>
      
      ${receiptData.orderNote ? `
        <div class="order-note">
          <p class="order-note-title">📝 SİPARİŞ NOTU:</p>
          <p class="order-note-text">${receiptData.orderNote}</p>
        </div>
      ` : ''}
      
      <div class="footer">
        <p>${receiptData.items.length} kalem / ${receiptData.items.reduce((sum, item) => sum + item.quantity, 0)} adet</p>
      </div>
    </body>
    </html>
  `;
};

/**
 * Kategori bazlı paket sipariş mutfak fişi HTML şablonu
 */
const createPackageKitchenReceiptByCategoryHtml = (
  receiptData: PackageKitchenReceiptData,
  categoryName: string,
  categoryItems: PackageKitchenOrderItem[]
): string => {
  const currentTime = receiptData.orderTime || new Date().toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Istanbul'
  });

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Paket Sipariş - ${categoryName}</title>
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: 'Poppins', sans-serif;
          font-size: 9pt;
          width: 72mm;
          max-width: 72mm;
          margin: 0;
          padding: 1mm 2mm 2mm 2mm;
          box-sizing: border-box;
          line-height: 1.2;
        }
        .header {
          text-align: center;
          border-bottom: 1px dashed #000;
          padding-bottom: 4px;
          margin-bottom: 4px;
        }
        .header h1 {
          margin: 0 0 2px 0;
          font-size: 11pt;
          font-weight: bold;
          text-align: center;
        }
        .category-badge {
          background-color: #000;
          color: #fff;
          padding: 2px 8px;
          font-size: 9pt;
          font-weight: bold;
          display: inline-block;
          margin: 2px 0;
          text-align: center;
        }
        .order-info {
          text-align: center;
          margin-bottom: 6px;
        }
        .order-number {
          font-size: 12pt;
          font-weight: bold;
          letter-spacing: 0;
          margin: 3px auto;
          padding: 2px 5px;
          border: 1px solid #000;
          display: inline-block;
          text-align: center;
        }
        .order-time {
          font-size: 9pt;
          color: #000;
          font-weight: bold;
          margin: 2px 0;
          text-align: center;
        }
        .customer-info {
          border: 1px solid #000;
          padding: 4px;
          margin: 4px 0;
          background-color: #f5f5f5;
        }
        .customer-info-title {
          font-size: 8pt;
          font-weight: bold;
          margin: 0 0 3px 0;
          color: #000;
          text-align: center;
          border-bottom: 1px dotted #000;
          padding-bottom: 2px;
        }
        .customer-name {
          font-size: 10pt;
          font-weight: bold;
          margin: 2px 0;
        }
        .customer-phone {
          font-size: 9pt;
          margin: 2px 0;
        }
        .customer-address {
          font-size: 9pt;
          margin: 3px 0 0 0;
          padding-top: 3px;
          border-top: 1px dotted #999;
          word-wrap: break-word;
        }
        .items-section {
          border-top: 1px dashed #000;
          border-bottom: 1px dashed #000;
          padding: 4px 0;
          margin: 4px 0;
        }
        .item {
          display: flex;
          align-items: flex-start;
          margin-bottom: 4px;
          padding-bottom: 3px;
          border-bottom: 1px dotted #999;
        }
        .item:last-child {
          border-bottom: none;
          margin-bottom: 0;
          padding-bottom: 0;
        }
        .item-quantity {
          font-size: 12pt;
          font-weight: bold;
          min-width: 24px;
          text-align: center;
          background-color: #000;
          color: #fff;
          padding: 1px 3px;
          margin-right: 5px;
        }
        .item-details {
          flex: 1;
          min-width: 0;
        }
        .item-name {
          font-size: 10pt;
          font-weight: bold;
          margin: 0;
          word-wrap: break-word;
        }
        .item-note {
          font-size: 8pt;
          color: #000;
          font-style: italic;
          margin: 2px 0 0 0;
          padding-left: 3px;
          border-left: 1px solid #000;
        }
        .sub-items {
          margin-left: 29px;
          margin-top: 2px;
        }
        .sub-item {
          font-size: 8pt;
          color: #000;
          margin: 1px 0;
          padding-left: 5px;
          border-left: 2px solid #666;
        }
        .sub-item-name {
          margin: 0;
        }
        .sub-item.extra-option {
          font-weight: bold;
          border-left-color: #000;
        }
        .order-note {
          border: 1px solid #000;
          padding: 4px;
          margin: 4px 0;
        }
        .order-note-title {
          font-size: 8pt;
          font-weight: bold;
          margin: 0 0 2px 0;
          color: #000;
        }
        .order-note-text {
          font-size: 9pt;
          margin: 0;
          font-style: italic;
        }
        .footer {
          text-align: center;
          margin-top: 4px;
          padding-top: 4px;
          border-top: 1px dashed #000;
        }
        .footer p {
          margin: 2px 0;
          font-size: 9pt;
          color: #000;
          font-weight: bold;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>${receiptData.isCancellationOrder ? '❌ İPTAL SİPARİŞ ❌' : receiptData.isAdditionalOrder ? '⚠️ İLAVE SİPARİŞ ⚠️' : /D-\d+$/.test(receiptData.shortCode || receiptData.orderNumber || '') ? '🖥️ KİOSK SİPARİŞİ - SELF SERVİS' : /P-\d+$/.test(receiptData.shortCode || receiptData.orderNumber || '') ? '🖥️ KİOSK SİPARİŞİ - PAKET' : receiptData.customerName?.includes('Masa Siparişi') ? '🪑 MASA SİPARİŞİ' : '📦 PAKET SİPARİŞ'}</h1>
        <div class="category-badge">${categoryName}</div>
      </div>
      
      <div class="order-info">
        <div class="order-number">${(() => { const code = receiptData.shortCode || receiptData.orderNumber || ''; const m = code.match(/[DP]-\d+$/); return m ? m[0] : code; })()}</div>
        <div class="order-time">${currentTime}</div>
      </div>
      
      <!-- Müşteri Bilgileri -->
      <div class="customer-info">
        <p class="customer-info-title">${/[DP]-\d+$/.test(receiptData.shortCode || receiptData.orderNumber || '') ? '🖥️ KİOSK BİLGİLERİ' : '📍 TESLİMAT BİLGİLERİ'}</p>
        <p class="customer-name">👤 ${receiptData.customerName}</p>
        ${receiptData.customerPhone ? `<p class="customer-phone">📞 ${receiptData.customerPhone}</p>` : ''}
        ${receiptData.customerAddress ? `<p class="customer-address">📍 ${receiptData.customerAddress}</p>` : ''}
      </div>
      
      <div class="items-section">
        ${categoryItems.map(item => `
          <div class="item">
            <div class="item-quantity">${item.quantity}</div>
            <div class="item-details">
              <p class="item-name">${item.name}</p>
              ${item.note ? `<p class="item-note">${item.note}</p>` : ''}
              ${item.subItems && item.subItems.length > 0 ? `
                <div class="sub-items">
                  ${item.subItems.map(subItem => `
                    <div class="sub-item${subItem.isExtraOption ? ' extra-option' : ''}">
                      <p class="sub-item-name">- ${subItem.name}${subItem.quantity > 1 ? ` x${subItem.quantity}` : ''}</p>
                    </div>
                  `).join('')}
                </div>
              ` : ''}
            </div>
          </div>
        `).join('')}
      </div>
      
      ${receiptData.orderNote ? `
        <div class="order-note">
          <p class="order-note-title">📝 SİPARİŞ NOTU:</p>
          <p class="order-note-text">${receiptData.orderNote}</p>
        </div>
      ` : ''}
      
      <div class="footer">
        <p>${categoryItems.length} kalem / ${categoryItems.reduce((sum, item) => sum + item.quantity, 0)} adet</p>
      </div>
    </body>
    </html>
  `;
};

// ═══════════════════════════════════════════════════════════
// 🪝 CUSTOM HOOK
// ═══════════════════════════════════════════════════════════

/**
 * Paket sipariş mutfak fişi yazdırma hook'u
 */
export const usePackageKitchenPrint = ({
  companyId,
  screenId,
  screenSettings,
  localServiceUrl: customLocalServiceUrl,
  onPrintError,
  onPrintSuccess,
}: PackageKitchenPrintServiceProps): PackageKitchenPrintServiceReturn => {
  
  // Yerel servis URL'sini al
  const localServiceUrl = customLocalServiceUrl || screenSettings?.localServiceUrl || DEFAULT_LOCAL_SERVICE_URL;
  
  // Debug log - sadece ilk mount'ta (spam önleme)
  useEffect(() => {
    console.log('[PackageKitchenPrintService] 🔌 Yerel servis URL:', localServiceUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Yazıcı ayarlarını al
  const getPrintingSettings = useCallback(() => {
    // Önce doğrudan geçirilen ayarları kontrol et (PackageOrderScreen'den)
    if (screenSettings?.printingMode) {
      console.log('[PackageKitchenPrintService] ✅ Yazdırma ayarları (doğrudan) yüklendi:', {
        printingMode: screenSettings.printingMode,
        defaultPrinter: screenSettings.defaultReceiptPrinter?.name || screenSettings.defaultReceiptPrinter,
        definedPrinters: screenSettings.definedPrinters?.length
      });
      return screenSettings;
    }
    
    // Yoksa PrintingSettingsJson'dan parse et
    const printingJson = screenSettings?.PrintingSettingsJson;
    
    if (!printingJson) {
      console.warn('[PackageKitchenPrintService] PrintingSettingsJson bulunamadı');
      return null;
    }
    
    try {
      const settings = typeof printingJson === 'string'
        ? JSON.parse(printingJson)
        : printingJson;
      
      console.log('[PackageKitchenPrintService] ✅ Yazdırma ayarları (JSON) yüklendi:', {
        printingMode: settings.printingMode,
        defaultPrinter: settings.defaultReceiptPrinter?.name,
        definedPrinters: settings.definedPrinters?.length
      });
      
      return settings;
    } catch (error) {
      console.error('[PackageKitchenPrintService] Yazdırma ayarları parse hatası:', error);
      return null;
    }
  }, [screenSettings]);

  /**
   * Tek yazıcıya paket sipariş mutfak fişi yazdır
   */
  const printPackageKitchenReceipt = useCallback(async (receiptData: PackageKitchenReceiptData): Promise<void> => {
    console.log('[PackageKitchenPrintService] 🖨️ Paket sipariş mutfak fişi yazdırılıyor...', {
      orderNumber: receiptData.orderNumber,
      customerName: receiptData.customerName,
      itemCount: receiptData.items.length
    });

    const settings = getPrintingSettings();
    
    if (!settings) {
      const errorMsg = 'Yazdırma ayarları bulunamadı. Lütfen ekran ayarlarından yazıcı tanımlayın.';
      console.error('[PackageKitchenPrintService] ❌', errorMsg);
      onPrintError?.(errorMsg);
      return;
    }

    // Varsayılan yazıcıyı kontrol et
    if (!settings.defaultReceiptPrinter) {
      const errorMsg = 'Varsayılan yazıcı ayarlanmamış. Lütfen ekran ayarlarından yazıcı seçin.';
      console.error('[PackageKitchenPrintService] ❌', errorMsg);
      onPrintError?.(errorMsg);
      return;
    }

    try {
      const rds = settings?.receiptDisplaySettings;
      const escPosData = {
        isKitchenReceipt: true,
        // İPTAL/İLAVE bayrakları local-service'e iletilmeli — yoksa başlık yazdırılmaz.
        isCancellationOrder: receiptData.isCancellationOrder === true,
        isAdditionalOrder: receiptData.isAdditionalOrder === true,
        items: receiptData.items.map(it => ({
          name: it.name,
          quantity: it.quantity,
          unitPrice: 0,
          totalPrice: 0,
          // ⬇ NOT/ACIKLAMA propagation — local-service mutfak şablonu
          //   (main.js 2367-2385) bu alanları zaten render ediyor; payload'a
          //   koymazsak per-item not/açıklama mutfak fişine düşmez.
          note: it.note,
          description: it.description,
          subItems: it.subItems
        })),
        orderNumber: receiptData.orderNumber,
        customerName: receiptData.customerName,
        companyName: receiptData.companyName || receiptData.header?.companyInfo?.name || rds?.companyName,
        orderTime: receiptData.orderTime,
        customerNote: receiptData.orderNote,
        invoiceNumber: receiptData.orderNumber,
        ...buildHeaderFields(receiptData.header, rds),
      };
      console.log('[PackageKitchenPrintService] 📤 Yazdırma isteği gönderiliyor:', {
        printerName: settings.defaultReceiptPrinter.name,
        printerId: settings.defaultReceiptPrinter.id
      });

      await sendPrintReceipt({
        printerId: settings.defaultReceiptPrinter.id,
        printerName: settings.defaultReceiptPrinter.name,
        receiptData: escPosData
      }, { baseUrl: localServiceUrl });

      console.log('[PackageKitchenPrintService] ✅ Paket sipariş mutfak fişi başarıyla yazdırıldı');
      onPrintSuccess?.(`Mutfak fişi yazdırıldı: ${receiptData.shortCode || receiptData.orderNumber}`);
    } catch (error: any) {
      console.error('[PackageKitchenPrintService] ❌ Yazdırma hatası:', error);
      onPrintError?.(`Mutfak fişi yazdırılamadı: ${error.message}`);
    }
  }, [getPrintingSettings, onPrintError, onPrintSuccess, localServiceUrl]);

  /**
   * Kategori bazlı paket sipariş mutfak fişi yazdır
   */
  const printPackageKitchenReceiptByCategory = useCallback(async (receiptData: PackageKitchenReceiptData): Promise<void> => {
    console.log('[PackageKitchenPrintService] 🖨️ Kategori bazlı paket sipariş mutfak fişi yazdırılıyor...', {
      orderNumber: receiptData.orderNumber,
      customerName: receiptData.customerName,
      itemCount: receiptData.items.length
    });

    const settings = getPrintingSettings();
    
    if (!settings) {
      console.warn('[PackageKitchenPrintService] ⚠️ Ayar bulunamadı, tek yazıcı moduna düşülüyor');
      return printPackageKitchenReceipt(receiptData);
    }

    // Yazdırma modu kontrolü
    if (settings.printingMode !== 'category') {
      console.log('[PackageKitchenPrintService] ℹ️ Tek yazıcı modu aktif');
      return printPackageKitchenReceipt(receiptData);
    }

    const categoryAssignments: { [categoryId: string]: string | null } = settings.categoryPrinterAssignments || {};
    const definedPrinters: DefinedPrinter[] = settings.definedPrinters || [];
    const adisyonPrinter: DefinedPrinter | null = settings.adisyonPrinter || null;

    // Detaylı debug log
    console.log('[PackageKitchenPrintService] 🔍 TÜM AYARLAR:', {
      categoryAssignments: JSON.stringify(categoryAssignments),
      definedPrinters: JSON.stringify(definedPrinters),
      adisyonPrinter: adisyonPrinter ? JSON.stringify(adisyonPrinter) : null,
    });
    // Her ürün için ayrı satır — collapsed array yerine net görünüm.
    receiptData.items.forEach((it, idx) => {
      const catId = it.categoryId;
      const mappedPrinter = catId ? categoryAssignments[catId] : null;
      const status = !catId
        ? '⚠️ KATEGORİ GUID YOK → varsayılan/adisyon yazıcıya düşer'
        : mappedPrinter
          ? `✅ "${catId}" → ${mappedPrinter}`
          : `⚠️ "${catId}" categoryAssignments'da yok → varsayılan/adisyon yazıcıya düşer`;
      console.log(`[PackageKitchenPrintService] 🔍 Ürün#${idx + 1} "${it.name}" | catId=${catId || '(YOK)'} | catName=${it.categoryName || '(YOK)'} | ${status}`);
    });

    const printerItemsMap: { [printerId: string]: PackageKitchenOrderItem[] } = {};
    const defaultItems: PackageKitchenOrderItem[] = [];

    receiptData.items.forEach(item => {
      const catId = item.categoryId;
      if (catId && categoryAssignments[catId]) {
        const printerId = categoryAssignments[catId]!;
        if (!printerItemsMap[printerId]) printerItemsMap[printerId] = [];
        printerItemsMap[printerId].push(item);
      } else {
        defaultItems.push(item);
      }
    });

    console.log('[PackageKitchenPrintService] 📂 Kategori bazlı dağılım:', {
      kategoriliUrunSayisi: Object.values(printerItemsMap).reduce((sum, items) => sum + items.length, 0),
      varsayilanUrunSayisi: defaultItems.length,
      yaziciSayisi: Object.keys(printerItemsMap).length,
      yazicilar: Object.keys(printerItemsMap),
    });

    const printPromises: Promise<any>[] = [];
    const rds = settings?.receiptDisplaySettings;

    for (const [printerId, items] of Object.entries(printerItemsMap)) {
      // categoryPrinterAssignments'taki value doğrudan yazıcı adı (KitchenPrintService ile aynı mantık)
      const printerName = printerId;
      const escPosData = {
        isKitchenReceipt: true,
        // İPTAL/İLAVE bayrakları local-service'e iletilmeli — yoksa başlık yazdırılmaz.
        isCancellationOrder: receiptData.isCancellationOrder === true,
        isAdditionalOrder: receiptData.isAdditionalOrder === true,
        // ⬇ NOT/ACIKLAMA propagation (kategori-bazlı yol)
        items: items.map(it => ({
          name: it.name,
          quantity: it.quantity,
          unitPrice: 0,
          totalPrice: 0,
          note: it.note,
          description: it.description,
          subItems: it.subItems,
        })),
        orderNumber: receiptData.orderNumber,
        customerName: receiptData.customerName,
        companyName: receiptData.companyName || receiptData.header?.companyInfo?.name || rds?.companyName,
        orderTime: receiptData.orderTime,
        customerNote: receiptData.orderNote,
        invoiceNumber: receiptData.orderNumber,
        ...buildHeaderFields(receiptData.header, rds),
      };
      console.log(`[PackageKitchenPrintService] 📤 ${items.length} ürün ${printerName} yazıcısına gönderiliyor...`);
      printPromises.push(
        sendPrintReceipt({
          printerId,
          printerName,
          receiptData: escPosData
        }, { baseUrl: localServiceUrl }).catch(err => {
          const detail = err.response?.data || err.message;
          console.error(`[PackageKitchenPrintService] ❌ ${printerName} yazıcısı hatası:`, detail);
          throw err;
        })
      );
    }

    if (defaultItems.length > 0) {
      const fallbackPrinter = adisyonPrinter || settings.defaultReceiptPrinter;
      if (fallbackPrinter) {
        const escPosData = {
          isKitchenReceipt: true,
          // İPTAL/İLAVE bayrakları local-service'e iletilmeli — yoksa başlık yazdırılmaz.
          isCancellationOrder: receiptData.isCancellationOrder === true,
          isAdditionalOrder: receiptData.isAdditionalOrder === true,
          // ⬇ NOT/ACIKLAMA propagation (varsayılan/adisyon yazıcı yolu)
          items: defaultItems.map(it => ({
            name: it.name,
            quantity: it.quantity,
            unitPrice: 0,
            totalPrice: 0,
            note: it.note,
            description: it.description,
            subItems: it.subItems,
          })),
          orderNumber: receiptData.orderNumber,
          customerName: receiptData.customerName,
          companyName: receiptData.companyName || receiptData.header?.companyInfo?.name || rds?.companyName,
          orderTime: receiptData.orderTime,
          customerNote: receiptData.orderNote,
          invoiceNumber: receiptData.orderNumber,
          ...buildHeaderFields(receiptData.header, rds),
        };
        console.log(`[PackageKitchenPrintService] 📤 ${defaultItems.length} ürün ${adisyonPrinter ? 'adisyon' : 'varsayılan'} yazıcıya gönderiliyor (${fallbackPrinter.name})...`);
        printPromises.push(
          sendPrintReceipt({
            printerId: fallbackPrinter.id,
            printerName: fallbackPrinter.name,
            receiptData: escPosData
          }, { baseUrl: localServiceUrl })
        );
      } else {
        console.warn(`[PackageKitchenPrintService] ⚠️ ${defaultItems.length} ürünün kategorisine yazıcı atanmamış ve adisyon/varsayılan yazıcı da yok!`);
      }
    }

    try {
      if (printPromises.length > 0) {
        await Promise.all(printPromises);
        console.log('[PackageKitchenPrintService] ✅ Tüm paket sipariş mutfak fişleri başarıyla yazdırıldı');
        onPrintSuccess?.(`${printPromises.length} adet mutfak fişi yazdırıldı`);
      } else {
        console.warn('[PackageKitchenPrintService] ⚠️ Yazdırılacak fiş bulunamadı, tek fiş moduna düşülüyor');
        return printPackageKitchenReceipt(receiptData);
      }
    } catch (error: any) {
      console.error('[PackageKitchenPrintService] ❌ Kategori bazlı yazdırma hatası:', error);
      onPrintError?.(`Mutfak fişleri yazdırılamadı: ${error.message}`);
    }
  }, [getPrintingSettings, printPackageKitchenReceipt, onPrintError, onPrintSuccess, localServiceUrl]);

  return {
    printPackageKitchenReceipt,
    printPackageKitchenReceiptByCategory,
  };
};

// ═══════════════════════════════════════════════════════════
// 📤 EXPORTS
// ═══════════════════════════════════════════════════════════

export default usePackageKitchenPrint;
